import { ClassNames } from '@emotion/react';
import { useTranslation } from '@presentation/utils';
import { useMemo } from 'react';
import type { FC } from 'react';
import { FaAt, FaLaptop, FaMapMarkedAlt, FaMobileAlt, FaPhoneAlt } from 'react-icons/fa';
import { DetailList } from '../DetailList';
import * as Styled from './styled';
import type { DetailListContactProps } from './types';

export * from './types';

export const DetailListContact: FC<DetailListContactProps> = (props) => {
  const {
    address,
    email,
    phoneNumber,
    mobilePhoneNumber,
    website,
    blurred = false,
    blurredChildren,
    labelMaxWidth,
    enableWhatsAppContact = false,
    onWhatsappClick,
    ...rest
  } = props;
  const { translate } = useTranslation();

  const items = useMemo(() => {
    return [
      ...(!enableWhatsAppContact
        ? [
            {
              label: translate('glossary.phone'),
              value: (
                <Styled.BlurContent blurred={blurred}>
                  {phoneNumber ? (
                    <Styled.LinkText target="_blank" href={`tel:${phoneNumber}`}>
                      {phoneNumber}
                    </Styled.LinkText>
                  ) : (
                    '-'
                  )}
                </Styled.BlurContent>
              ),
              labelIcon: <FaPhoneAlt />,
            },
            {
              label: translate('glossary.mobilePhone'),
              value: (
                <Styled.BlurContent blurred={blurred}>
                  {mobilePhoneNumber ? (
                    <Styled.LinkText target="_blank" href={`tel:${mobilePhoneNumber}`}>
                      {mobilePhoneNumber}
                    </Styled.LinkText>
                  ) : (
                    '-'
                  )}
                </Styled.BlurContent>
              ),
              labelIcon: <FaMobileAlt />,
            },
            {
              label: translate('glossary.email'),
              value: (
                <Styled.BlurContent blurred={blurred}>
                  {email ? (
                    <Styled.LinkText target="_blank" href={`mailto:${email}`}>
                      {email}
                    </Styled.LinkText>
                  ) : (
                    '-'
                  )}
                </Styled.BlurContent>
              ),
              labelIcon: <FaAt />,
            },
            {
              label: translate('glossary.website'),
              value: (
                <Styled.BlurContent blurred={blurred}>
                  {website?.url ? (
                    <Styled.LinkText target="_blank" href={website?.url}>
                      {website?.label ?? website?.url}
                    </Styled.LinkText>
                  ) : (
                    website?.label ?? '-'
                  )}
                </Styled.BlurContent>
              ),
              labelIcon: <FaLaptop />,
            },
          ]
        : []),
      {
        label: translate('glossary.address'),
        value: (
          <Styled.BlurContent blurred={blurred}>
            {address?.url ? (
              <Styled.LinkText target="_blank" href={address?.url}>
                {address?.label ?? address?.url}
              </Styled.LinkText>
            ) : (
              <Styled.AddressText>{address?.label ?? '-'}</Styled.AddressText>
            )}
          </Styled.BlurContent>
        ),
        labelIcon: <FaMapMarkedAlt />,
      },
    ];
  }, [
    address?.label,
    address?.url,
    blurred,
    email,
    enableWhatsAppContact,
    mobilePhoneNumber,
    phoneNumber,
    translate,
    website?.label,
    website?.url,
  ]);

  return (
    <Styled.Wrapper>
      {blurred && <Styled.BlurOverlay>{blurredChildren}</Styled.BlurOverlay>}
      <ClassNames>
        {({ css }) => (
          <DetailList
            labelClassName={css`
              ${labelMaxWidth && ` max-width: ${labelMaxWidth}; `}
            `}
            items={items}
            enableWhatsAppContact={enableWhatsAppContact}
            onWhatsappClick={onWhatsappClick}
            {...rest}
          />
        )}
      </ClassNames>
    </Styled.Wrapper>
  );
};
